import { useCallback, useEffect, useMemo } from 'react';
import { useDownloadQuery } from '@/contexts/downloadQueryContextBase';
import { useDoctorActivityQuery } from '@/hooks/useDoctorActivityQuery';
import { useToast } from '@/contexts/toastContextBase';
import { getActivities, getActivitiesAll } from '@/api/activities';
import { downloadBlob } from '@/utils/download';
import {
  buildDoctorDownloadRequest,
  getDoctorDownloadModeConfig,
  type DownloadMode,
} from './downloadWorkflow';

export function useDoctorDownloadController(mode: DownloadMode) {
  const { showToast } = useToast();
  const config = useMemo(() => getDoctorDownloadModeConfig(mode), [mode]);

  const {
    query,
    preview,
    isLoadingPreview,
    downloadProgress,
    allPatients,
    setPatients,
    setActivityTypes,
    setVibrationModes,
    setDateRange,
    clearFilters,
    setAllPatients,
    setAllActivities,
    loadPreview,
    setDownloadProgress,
    hasSelection,
    selectedActivities,
    selectedCount,
    patientCount,
    selectedActivityIds,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
  } = useDownloadQuery();

  const {
    patients,
    activities,
    isLoadingPatients,
    isLoadingActivities,
  } = useDoctorActivityQuery({
    patientIds: query.patientIds,
    shouldLoad: hasSelection,
    loadActivities: config.loadActivities === 'raw' ? getActivitiesAll : getActivities,
  });

  useEffect(() => {
    setAllPatients(patients);
  }, [patients, setAllPatients]);

  useEffect(() => {
    setAllActivities(activities);
  }, [activities, setAllActivities]);

  useEffect(() => {
    if (hasSelection && allPatients.length > 0) {
      loadPreview();
    }
  }, [hasSelection, query, allPatients.length, loadPreview]);

  const handleDownload = useCallback(async () => {
    if (selectedActivities.length === 0) return;

    const { request, uniquePatientCount } = buildDoctorDownloadRequest(
      selectedActivities,
      allPatients
    );
    setDownloadProgress({ status: 'fetching', current: 0, total: uniquePatientCount });

    try {
      setDownloadProgress({
        status: 'fetching',
        current: Math.floor(uniquePatientCount / 2),
        total: uniquePatientCount,
      });

      const blob = await config.downloadAction(request);

      setDownloadProgress({
        status: 'packaging',
        current: uniquePatientCount,
        total: uniquePatientCount,
      });

      downloadBlob(blob, `${config.filenamePrefix}-${new Date().toISOString().split('T')[0]}.zip`);

      setDownloadProgress({
        status: 'complete',
        current: uniquePatientCount,
        total: uniquePatientCount,
      });
      showToast(
        'success',
        `Downloaded ${selectedActivities.length} ${config.downloadLabel} from ${uniquePatientCount} patients`
      );

      setTimeout(() => {
        setDownloadProgress({ status: 'idle', current: 0, total: 0 });
      }, 2000);
    } catch (error) {
      setDownloadProgress({
        status: 'error',
        current: 0,
        total: 0,
        message: error instanceof Error ? error.message : 'Download failed',
      });
      showToast('error', `Failed to download ${config.downloadLabel}`);
    }
  }, [
    selectedActivities,
    allPatients,
    setDownloadProgress,
    showToast,
    config,
  ]);

  return {
    config,
    query,
    preview,
    isLoadingPreview,
    downloadProgress,
    allPatients,
    hasSelection,
    selectedCount,
    patientCount,
    selectedActivityIds,
    isLoadingPatients,
    isLoadingActivities,
    setPatients,
    setActivityTypes,
    setVibrationModes,
    setDateRange,
    clearFilters,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
    handleDownload,
  };
}
